"use client";

import { PRIMARY_COLOR } from "../../layout/LayoutConstants";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import PillButton from "@/app/shared/PillButton";
import { AdVertical } from "@/sell/models/AdVerticals";
import { CreateAd } from "@/sell/actions/CreateAd";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useLazySession } from "@/app/layout/useLazySession";

interface SellPrimaryCategoryProps {
  title: string;
  subtitle: string[];
  icon: string;
  vertical: AdVertical;
  pricingSubtext?: string;
}

function SellPrimaryCategory({
  title,
  subtitle,
  icon,
  vertical,
  pricingSubtext,
}: SellPrimaryCategoryProps) {
  const { isLoaded, isAuthenticated } = useLazySession();
  const [isCreating, setIsCreating] = useState(false);
  const router = useRouter();
  return (
    <Box
      sx={{
        background: "white",
        borderRadius: 10,
        boxShadow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 2,
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 100,
          img: {
            objectFit: "contain",
          },
        }}
      >
        <Image
          src={`/icons/categories/${icon}.png`}
          alt={title}
          style={{ borderRadius: 10 }}
          unoptimized={true}
          fill
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: "bold" }}>
          {title}
        </Typography>
        <Box>
          {subtitle.map((line, index) => (
            <Typography key={index}>{line}</Typography>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="body1">
          from{" "}
          <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
            $29
          </Typography>{" "}
          until sold
        </Typography>
        <PillButton
          primary={true}
          fullWidth
          sx={{
            "&&&": {
              borderColor: PRIMARY_COLOR,
              color: PRIMARY_COLOR,
              fontWeight: "bold",
            },
          }}
          loading={isCreating}
          disabled={isCreating || !isLoaded}
          onClick={async () => {
            if (!isAuthenticated) {
              router.push("/auth?returnUrl=/sell");
              return;
            }

            setIsCreating(true);
            const ad = await CreateAd({
              vertical: vertical,
            });
            console.log("Ad created", ad);
            router.push(`/sell/ad/${ad.id}/details`);
          }}
        >
          Start Selling
        </PillButton>
        <Typography variant="body2">{pricingSubtext || <>&nbsp;</>}</Typography>
      </Box>
    </Box>
  );
}

export default function SellLandingCategories() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        sx={{ textAlign: "center", color: "white" }}
      >
        What are you selling today?
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 6,
          padding: 2,
        }}
      >
        <SellPrimaryCategory
          title="Horses"
          subtitle={["for sale, lease, wanted & ", "stallions at stud"]}
          icon="horse"
          vertical="horse"
          pricingSubtext="Under $5k? Your ad is free"
        />
        <SellPrimaryCategory
          title="Floats & Trucks"
          subtitle={["including trailers, tow vehicles & motorhomes"]}
          icon="trailer"
          vertical="vehicle"
          pricingSubtext="Under $5k? Your ad is free"
        />
        <SellPrimaryCategory
          title="Saddlery, Tack & More"
          subtitle={["including equipment, apparel, ", "products & more"]}
          icon="saddle"
          vertical="saddlery-and-more"
          pricingSubtext="Under $3k? Your ad is free"
        />
        <SellPrimaryCategory
          title="Property, Services +"
          subtitle={["anything else equine related for sale, lease or wanted"]}
          icon="property"
          vertical="property"
        />
      </Box>
    </Box>
  );
}
